import { Link } from 'react-router';
import { Icon } from '@dayon/design-system';
import { formatCount } from '@dayon/money';
import type { LicenceWarning } from '@dayon/contracts';

/* ── The 60-day licence warning ──────────────────────────────────────────────
   "Licence expiry drives a 60-day advance warning on the dashboard."

   The window is computed in SQL and arrives already filtered: `licences` holds
   only the brands inside 60 days or already past, scoped to the caller's brand
   restriction and the console's brand selector. An empty array is the calm
   state and NOTHING renders — a permanent band saying "no licence expires
   soon" is a band people stop reading, and then it is not a warning.

   Platform settings renders the same fact on the brands table and in its own
   notice; it is now the SECOND place this appears rather than the only one, so
   the wording, the date format and the day count below are deliberately its
   wording, its format and its arithmetic (`day()` and `daysUntil()` in
   PlatformSettings.tsx). Two vocabularies for one obligation would read as two
   different obligations.
   ──────────────────────────────────────────────────────────────────────────── */


/*
 * EXTRACTED so both dashboard views can draw it.
 *
 * `/` remembers which of its three views you last used. Rendering this only in
 * the Split view meant an operator whose remembered view is Live Ops never met
 * the warning — and a warning you have to switch views to find is not an
 * advance warning. `/api/dashboard` and `/api/dashboard/live` both return
 * `licences` from the one query, so the two views cannot disagree about it.
 */
const licenceDayFmt = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  /* A licence expires on the regulator's calendar, and the regulator is in
     Manila. Rendering it in the reader's own zone would move the date. */
  timeZone: 'Asia/Manila',
});
const licenceDay = (iso: string): string => licenceDayFmt.format(new Date(iso));
/** Whole days from now, as PlatformSettings counts them. Negative once past. */
const daysUntil = (iso: string): number =>
  Math.round((new Date(iso).getTime() - Date.now()) / 86_400_000);

export function LicenceWarnings({
  licences,
  canOpenSettings,
}: {
  licences: LicenceWarning[];
  canOpenSettings: boolean;
}) {
  if (licences.length === 0) return null;
  return (
    <section className="licence-warnings stack" aria-labelledby="licence-warning-heading">
      <h3 id="licence-warning-heading" className="sr-only">
        Brand licences needing attention
      </h3>
      {licences.map((l) => {
        const days = daysUntil(l.expiresAt);
        return (
          <div
            key={l.brandId}
            className={l.expired ? 'notice licence-lapsed' : 'notice'}
            data-testid={l.expired ? 'licence-lapsed' : 'licence-expiring'}
          >
            <Icon name={l.expired ? 'warning-octagon' : 'warning-circle'} />
            <div>
              {l.expired ? (
                /* NOT a warning any more. A warning is about something that has
                   not happened; this brand is taking bets on a licence that has
                   run out, which is a regulatory breach happening now. */
                <>
                  <strong>{l.brandName} is trading past its licence.</strong> It expired on{' '}
                  <span className="tabular">{licenceDay(l.expiresAt)}</span> —{' '}
                  {formatCount(Math.abs(days))} day{Math.abs(days) === 1 ? '' : 's'} ago. This is
                  not a renewal reminder: every bet the brand takes now is taken without one.
                </>
              ) : (
                <>
                  <strong>{l.brandName}&rsquo;s licence expires</strong> inside the 60-day
                  advance-warning window on{' '}
                  <span className="tabular">{licenceDay(l.expiresAt)}</span> — in{' '}
                  {formatCount(days)} day{days === 1 ? '' : 's'}. Renewal is a regulator lead time,
                  not an operational one — start it now.
                </>
              )}{' '}
              {l.licenceRef ? (
                <>
                  Licence <span className="tabular">{l.licenceRef}</span>.
                </>
              ) : (
                <>No licence reference is recorded against the brand.</>
              )}{' '}
              {/* The dashboard is players.view, held by every role but the
                  PAGCOR Regulator, which never sees this screen. Platform
                  settings is platform.view, which not every reader here holds —
                  a Support Agent does not — so the copy must not promise the
                  reader can act there. */}
              {canOpenSettings ? (
                <Link to="/settings">Licences are managed in Platform settings.</Link>
              ) : (
                <>
                  Licences are recorded in <Link to="/settings">Platform settings</Link>, which
                  needs the <code>platform.view</code> permission your role does not hold — pass
                  this on to whoever renews them.
                </>
              )}
            </div>
          </div>
        );
      })}
    </section>
  );
}
